import React, { useState, useEffect, useRef } from 'react';
import {
  ScanLine, Keyboard, Check, X, AlertCircle,
  Clock, User, Tag, Shield, History, ChevronDown, ChevronUp, Lock, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { api } from '../services/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  reservado: { label: 'Reservado', color: 'bg-amber-100 text-amber-700', icon: Clock },
  utilizado: { label: 'Utilizado', color: 'bg-green-100 text-green-700', icon: Check },
  expirado:  { label: 'Expirado',  color: 'bg-gray-100 text-gray-500', icon: X },
};

export default function ValidarCupom({ business }) {
  const [mode, setMode] = useState('input'); // input | lookup | confirm | success | pin-setup
  const [voucherCode, setVoucherCode] = useState('');
  const [voucher, setVoucher] = useState(null);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [hasPin, setHasPin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (business?.id) {
      api.get(`/v2/vouchers/lojista/has-pin/${business.id}`)
        .then(res => setHasPin(res.data.has_pin))
        .catch(() => setHasPin(false));
    }
  }, [business?.id]);

  const handleLookup = async () => {
    if (!voucherCode.trim()) return;
    setLoading(true);
    try {
      const res = await api.get(`/v2/vouchers/lojista/lookup/${voucherCode.toUpperCase()}`);
      setVoucher(res.data);
      setMode('lookup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Voucher não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!pin || pin.length !== 4) {
      toast.error('Digite o PIN de 4 dígitos');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/v2/vouchers/lojista/validate', {
        voucher_code: voucher.voucher_code,
        pin,
        business_id: business.id,
      });
      setVoucher(res.data.voucher);
      setMode('success');
      toast.success('Cupom validado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na validação');
    } finally {
      setLoading(false);
      setPin('');
    }
  };

  const handleSetupPin = async () => {
    if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      toast.error('PIN deve ter exatamente 4 dígitos numéricos');
      return;
    }
    setLoading(true);
    try {
      await api.post('/v2/vouchers/lojista/pin', { business_id: business.id, pin: newPin });
      toast.success('PIN configurado com sucesso!');
      setHasPin(true);
      setMode('input');
      setNewPin('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao configurar PIN');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (history) { setShowHistory(!showHistory); return; }
    try {
      const res = await api.get(`/v2/vouchers/lojista/history/${business.id}`);
      setHistory(res.data);
      setShowHistory(true);
    } catch {
      toast.error('Erro ao carregar histórico');
    }
  };

  const reset = () => {
    setMode('input');
    setVoucher(null);
    setVoucherCode('');
    setPin('');
  };

  // PIN setup required
  if (hasPin === false && mode !== 'pin-setup') {
    return (
      <Card data-testid="validar-cupom">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#56007F]" />
            <h3 className="text-lg font-bold">Validar Cupom</h3>
          </div>
          <div className="text-center py-6 space-y-3">
            <Lock className="w-10 h-10 text-gray-300 mx-auto" />
            <p className="text-sm text-gray-600">Configure um PIN de 4 dígitos para validar cupons.</p>
            <p className="text-xs text-gray-400">O PIN garante que apenas pessoas autorizadas confirmem resgates.</p>
            <Button onClick={() => setMode('pin-setup')} data-testid="setup-pin-btn">
              <Settings className="w-4 h-4 mr-1.5" /> Configurar PIN
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // PIN setup form
  if (mode === 'pin-setup') {
    return (
      <Card data-testid="validar-cupom">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#56007F]" />
            <h3 className="text-lg font-bold">{hasPin ? 'Alterar' : 'Configurar'} PIN</h3>
          </div>
          <p className="text-sm text-gray-600">Escolha 4 dígitos numéricos para validar resgates de cupom.</p>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            placeholder="0000"
            value={newPin}
            onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="text-center text-2xl tracking-[0.5em] max-w-[180px] mx-auto"
            data-testid="pin-input"
          />
          <div className="flex gap-2 justify-center">
            <Button onClick={handleSetupPin} disabled={loading || newPin.length !== 4} data-testid="save-pin-btn">
              <Check className="w-4 h-4 mr-1.5" /> Salvar PIN
            </Button>
            {hasPin && (
              <Button variant="outline" onClick={() => setMode('input')}>Cancelar</Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="validar-cupom">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#56007F]" />
            <h3 className="text-lg font-bold">Validar Cupom</h3>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => setMode('pin-setup')} title="Alterar PIN">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={loadHistory} title="Histórico">
              <History className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Input mode */}
        {mode === 'input' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Digite o código do voucher do cliente:</p>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={voucherCode}
                onChange={e => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="VCH-XXXX"
                className="text-center text-lg tracking-wider font-mono uppercase"
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                data-testid="voucher-input"
              />
              <Button onClick={handleLookup} disabled={loading || !voucherCode.trim()} data-testid="lookup-btn">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Keyboard className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Lookup result */}
        {mode === 'lookup' && voucher && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-testid="voucher-details">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="font-mono text-sm">{voucher.voucher_code}</Badge>
                <Badge variant="outline" className={STATUS_CONFIG[voucher.status]?.color || ''}>{voucher.status}</Badge>
              </div>
              <p className="font-semibold text-sm">{voucher.coupon_titulo}</p>
              <p className="text-xl font-black text-amber-600">
                {voucher.tipo_desconto === 'percentual' ? `${voucher.coupon_desconto}% OFF` : `R$ ${voucher.coupon_desconto} OFF`}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <User className="w-3.5 h-3.5" />
                <span>{voucher.user_name || voucher.user_email}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                <span>Expira: {new Date(voucher.expires_at).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>

            {voucher.status === 'reservado' ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 text-center">Digite seu PIN para confirmar:</p>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="PIN"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="text-center text-2xl tracking-[0.5em] max-w-[180px] mx-auto"
                  onKeyDown={e => e.key === 'Enter' && handleValidate()}
                  data-testid="confirm-pin"
                />
                <div className="flex gap-2 justify-center">
                  <Button onClick={handleValidate} disabled={loading || pin.length !== 4} className="bg-green-600 hover:bg-green-700" data-testid="confirm-validate-btn">
                    <Check className="w-4 h-4 mr-1.5" /> Confirmar Uso
                  </Button>
                  <Button variant="outline" onClick={reset}>Cancelar</Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-2">
                <p className="text-sm text-red-500">
                  {voucher.status === 'utilizado' ? 'Este voucher já foi utilizado.' :
                   voucher.status === 'expirado' ? 'Este voucher expirou.' : 'Voucher inválido.'}
                </p>
                <Button variant="outline" onClick={reset}>Verificar outro</Button>
              </div>
            )}
          </div>
        )}

        {/* Success */}
        {mode === 'success' && (
          <div className="text-center py-4 space-y-3" data-testid="validation-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h4 className="text-lg font-bold text-green-700">Cupom Validado!</h4>
            <p className="text-sm text-gray-600">
              {voucher?.coupon_titulo} — {voucher?.tipo_desconto === 'percentual' ? `${voucher?.coupon_desconto}%` : `R$${voucher?.coupon_desconto}`} de desconto
            </p>
            <p className="text-xs text-gray-400">Cliente: {voucher?.user_name || voucher?.user_email}</p>
            <Button variant="outline" onClick={reset} className="mt-2" data-testid="validate-another-btn">
              Validar outro cupom
            </Button>
          </div>
        )}

        {/* History */}
        {showHistory && history && (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-1.5">
                <History className="w-4 h-4" /> Histórico de Resgates
              </h4>
              <div className="flex gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  {history.stats.reservado} reservados
                </Badge>
                <Badge variant="secondary" className="text-[10px] bg-green-100">
                  {history.stats.utilizado} utilizados
                </Badge>
              </div>
            </div>
            {history.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Código</TableHead>
                    <TableHead className="text-xs">Cliente</TableHead>
                    <TableHead className="text-xs">Cupom</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.items.slice(0, 20).map(v => {
                    const cfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.reservado;
                    return (
                      <TableRow key={v.voucher_id}>
                        <TableCell className="font-mono text-xs">{v.voucher_code}</TableCell>
                        <TableCell className="text-xs">{v.user_name || v.user_email}</TableCell>
                        <TableCell className="text-xs truncate max-w-[120px]">{v.coupon_titulo}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-400">
                          {new Date(v.validated_at || v.reserved_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">Nenhum resgate registrado</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
